import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// import useClickOutside from "../../../../hooks/useClickOutside";
import { TablesDropdown, TablePopups } from "../../../../common/AppBarComponents";
import { useTableDropdownLogic } from "../../../../hooks/useTableDropdownLogic";
import "../../../../common/AppBar.css";
import { Button, CancelButton } from "../../../../common/Buttons";
import AddElementsDropdown from "./AddElementsDropdown";
import {
  openCopyReplace,
  openDelete,
  openMove,
} from "../../../../redux/previewSlice";
import { getProcedureRevisionById } from "../../../../redux/procedureRevisionSlice";
import { buildPermissionContext, PERMISSIONS } from "../../../../util/permissions";
import { openTaskPopup, setNewTask } from "../../../../redux/taskSlice";
import OpenTasksPopup from "../../../Tasks/Popups/OpenTasksPopup";
import useApiTabLock from "../../../../hooks/useApiTabLock";
import { useRevisionLock } from "../../../../contexts/RevisionLockContext";

import { fetchProcedureModule } from "../../../../redux/moduleSlice";
import { hasModulePermission } from "../../../../util/AppUtils";
import { showSnackbar } from "../../../../redux/snackbarSlice";
import VersionHistoryPopup from "../../../../common/VersionHistoryPopup/VersionHistoryPopup";
import { useVersionHistoryDataFromNodes } from "../../../../common/VersionHistoryPopup/useVersionHistoryDataFromNodes";
import { getVersionHistoryAuditPreviewPath } from "../../../../common/VersionHistoryPopup/getVersionHistoryAuditPreviewPath";

const NodeAppBar = ({
  title = "null",
  isSection = false,
  isStep = false,
  isPart = false,
  isSubstep = false,
  selectedSubstepTitle = "",
  isSelectedPage = false,
  isSectionElement = false,
  editSubstepbtn = false,
  buttons = [],
  onAddElement = null,
  onEditSection = null,
  onEditPart = null,
  onEditStep = null,
  onEditSubstep = null,
  onEditSectionElement = null,
  partNumber = null,
  revision = null,
  type = null,
  partId = null,
  revisionId = null,
  sectionId = null,
  sectionIndex = null,
  stepId = null,
  stepIndex = null,
  globalStepNumber = null,
  substepId = null,
  substepIndex = null,
  substepLetter = null,
  version_id_ref = null,
  ltree_path = null,
  is_deleted = false,
  isLocked = false,
  /** Enables Tables → Version History on view-section / view-step / view-substep / view-childnode when nodes or this id are available. */
  versionHistoryFallbackVersionId = null,
  /** When set, common VersionHistoryPopup "Node History" filter matches this id in `versionHistoryNodeIdField` on each row. */
  versionHistoryNodeId = null,
  versionHistoryNodeIdField = "element_id",
  /** Extra ids for Node History matching (merged with versionHistoryNodeId). */
  versionHistoryMatchIds = null,
  /** 1-based section display index for Node History text fallback (view-section). */
  versionHistorySectionOrdinal = null,
  /** 1-based global step index for Node History text fallback (view-step / view-substep). */
  versionHistoryStepOrdinal = null,
  /** Alphanumeric substep suffix (e.g. "A") with step ordinal for "Substep 3A" audit text on view-substep. */
  versionHistorySubstepLetterSuffix = null,
  /** Global media ordinal + regex phrase for sparse Node History on view-childnode (Attachment N, Video N, …). */
  versionHistoryLeafMediaOrdinal = null,
  versionHistoryLeafMediaPhraseRegex = null,
  /** When true (e.g. view-revision part screen), Version History opens on Revision History with filter dropdown disabled — matches Document.js Tables → Version History. */
  historyFilterDropdownDisabled = false,
}) => {
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    partId: urlPartId,
    part_id,
    revision: urlRevision,
    partnumber: urlPartnumber,
    revisionId: urlRevisionId,
    revision_id,
    section_id_ref,
    step_id_ref,
    substep_id_ref: urlSubstepIdRef,
    child_node_id,
  } = useParams(); // Get URL parameters
  const authUser = useSelector((state) => state.auth.user);
  const allRevisionNodes = useSelector(
    (state) => state.procedureRevision.allRevisionNodes,
  );
  const permissionCtx = buildPermissionContext(authUser);


  const {
    showTablesDropdown,
    setShowTablesDropdown,
    tablesDropdownRef,
    showTablePopup,
    handleDrpdnClick,
    closeTablePopup,
  } = useTableDropdownLogic();

  const canAccessTables = permissionCtx.permissions?.[PERMISSIONS.EDIT] === true;

  const canOpenVersionHistory =
    canAccessTables &&
    (!!versionHistoryFallbackVersionId ||
      (Array.isArray(allRevisionNodes) && allRevisionNodes.length > 0));

  const [module_type, setModule_type] = useState(null);
  const hasmodluepermission = hasModulePermission(module_type);
  const canEdit = permissionCtx.permissions?.Edit === true && !is_deleted && hasmodluepermission;
  // Task creation: allow when user has Edit and node is not deleted (no module fetch dependency)
  const canCreateTask = permissionCtx.permissions?.Edit === true && !is_deleted;

  const [openTasksState, setOpenTasksState] = useState({
    isOpen: false,
    ltree_path: null,
    deleteNodeData: null,
    deleteDocumentData: null,
    copyData: null,
    moveData: null,
    operation: null,
    title: null
  });
  // Get substep_id_ref from query parameters if not in URL
  const searchParams = new URLSearchParams(location.search);
  const querySubstepIdRef = searchParams.get("substep_id_ref");
  const effectiveSubstepIdRef =
    substepId || querySubstepIdRef || urlSubstepIdRef;

  // Use props if provided, otherwise fall back to URL params
  const effectivePartId = partId || urlPartId || part_id;
  const effectiveRevisionId = revisionId || urlRevisionId || revision_id;
  const effectivePartNumber = partNumber || urlPartnumber;
  const effectiveRevision = revision || urlRevision;

  const canViewDoc = permissionCtx.permissions?.[PERMISSIONS.VIEW] === true;
  const canEditDoc = permissionCtx.permissions?.[PERMISSIONS.EDIT] === true;
  const canNavigateVersionPreview = canViewDoc || canEditDoc;

  const {
    mergedVersionHistories,
    allVersionHistories,
    versionHistoryTableLoading,
  } = useVersionHistoryDataFromNodes({
    isOpen: showVersionHistory,
    revisionId: effectiveRevisionId,
    partId: effectivePartId,
    allRevisionNodes,
    fallbackVersionId: versionHistoryFallbackVersionId,
    nodesReady: true,
  });

  const versionHistoryPopupTitle = useMemo(() => {
    if (
      effectivePartNumber != null &&
      String(effectivePartNumber).trim() !== "" &&
      effectiveRevision != null &&
      String(effectiveRevision).trim() !== ""
    ) {
      return `Version History for ${String(effectivePartNumber).trim()} Rev ${String(
        effectiveRevision,
      ).trim()}`;
    }
    if (
      effectivePartNumber != null &&
      String(effectivePartNumber).trim() !== ""
    ) {
      return `Version History for ${String(effectivePartNumber).trim()}`;
    }
    return "Version History";
  }, [effectivePartNumber, effectiveRevision]);

  const versionHistoryBreadcrumbFallback = useMemo(() => {
    const pn =
      effectivePartNumber != null ? String(effectivePartNumber).trim() : "";
    const revPart =
      effectiveRevision != null && String(effectiveRevision).trim() !== ""
        ? `Rev ${String(effectiveRevision).trim()}`
        : "";
    return [pn, revPart].filter(Boolean).join(" > ");
  }, [effectivePartNumber, effectiveRevision]);

  // Use revision ID as route key to lock entire revision across all pages
  // This ensures all pages within the same revision (Document, sections, steps, substeps, canvases, etc.)
  // share the same lock across tabs
  // IMPORTANT: Use consistent routeKey across all revision-related pages
  const routeKey = `revision_${effectiveRevisionId}`;

  // Tab lock management - only one tab can perform actions
  // Use revision-specific key to ensure all pages within the same revision share the same lock
  const { isLocked: isApiLocked, isActiveTab, lockedBy } = useApiTabLock(`revision_${effectiveRevisionId}`);

  // Tab order lock from provider
  const revisionLock = useRevisionLock();
  const isFirstTabByOrder =
    revisionLock?.revisionIdFromPath === effectiveRevisionId ? revisionLock.isFirstTab : false;
  const isLockedByOrder =
    revisionLock?.revisionIdFromPath === effectiveRevisionId ? revisionLock.isLocked : true;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch procedure module
  useEffect(() => {
    const fetchModule = async () => {
      if (effectiveRevisionId) {
        try {
          const result = await dispatch(
            fetchProcedureModule({ procedure_id_ref: effectiveRevisionId })
          );
          if (result.payload.success && result.payload.results.length > 0) {
            setModule_type(result.payload.results[0].type_name);
          } else {
            setModule_type(undefined);
          }
        } catch (error) {
          console.error("Error fetching procedure module:", error);
        }
      }
    };

    if (effectiveRevisionId) {
      fetchModule();
    }
  }, [effectiveRevisionId, dispatch]);

  // Helper function to handle cancel button - navigate without unlock
  const handleCancel = async () => {
    // ALWAYS set flag when canceling from node view pages
    // This indicates we're returning back to Document.js
    sessionStorage.setItem(`returningFromViewRevision_${effectiveRevisionId}`, 'true');
    // Clean up the fromDocument flag if it exists
    const fromDocument = sessionStorage.getItem(`fromDocument_${effectiveRevisionId}`) === 'true';
    if (fromDocument) {
      sessionStorage.removeItem(`fromDocument_${effectiveRevisionId}`);
    }
    
    // Dispatch custom event to notify Document.js
    window.dispatchEvent(new CustomEvent('navigation-from-view-revision', {
      detail: { revisionId: effectiveRevisionId, timestamp: Date.now() }
    }));
    
    // Navigate to revision-submission
    navigate(
      `/revision-submission/${effectivePartId}/${effectiveRevision}/type/${effectivePartNumber}/${effectiveRevisionId}/redirect`
    );
  };

  // Helper function to check for open tasks and proceed with delete
  const handleDeleteWithTaskCheck = async (deleteNodeData, deleteDocumentData) => {
    const nodeName = deleteNodeData?.nodeTitle || "Node";
    const popupTitle = `Pending Tasks for ${effectivePartNumber}, Rev ${effectiveRevision}, ${nodeName}`;
    setOpenTasksState({
      isOpen: true,
      ltree_path: ltree_path,
      deleteNodeData: deleteNodeData,
      deleteDocumentData: deleteDocumentData,
      operation: "delete",
      title: popupTitle
    });
  };

  // This is using for copying the node as well as the copy and replace operation for the document.
  const handleCopyWithTaskCheck = async (copyPayload) => {
    const nodeName = copyPayload?.sourceNode?.nodeTitle;
    const popupTitle = `Pending Tasks for ${effectivePartNumber}, Rev ${effectiveRevision}${nodeName ? `, ${nodeName}` : ''}`;
    setOpenTasksState({
      isOpen: true,
      ltree_path: ltree_path,
      copyData: copyPayload,
      operation: "copy",
      title: popupTitle
    });
  };

  const handleMoveWithTaskCheck = async (movePayload) => {
    const nodeName = movePayload?.moveNode?.nodeTitle || "Node";
    const popupTitle = `Pending Tasks for ${effectivePartNumber}, Rev ${effectiveRevision}, ${nodeName}`;
    setOpenTasksState({
      isOpen: true,
      ltree_path: ltree_path,
      moveData: movePayload,
      operation: "move",
      title: popupTitle
    });
  };

  return (
    <>
      <header
        className="appbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px",
        }}
      >
        {/* Left Side: Cancel & Task Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <CancelButton
            onClick={handleCancel}
          />

          <Button
            onClick={() => {
              if (!canCreateTask) return;
              dispatch(openTaskPopup());
              dispatch(setNewTask({}))
            }}
            size="sm"
            disabled={!canCreateTask}
            style={{
              opacity: canCreateTask ? 1 : 0.5,
              cursor: canCreateTask ? "pointer" : "not-allowed",
            }}
            title={
              !canCreateTask
                ? "You do not have permission to create tasks."
                : undefined
            }
          >
            + Task
          </Button>

          <Button
            onClick={() => canEdit && !isLocked && setShowAddDropdown(!showAddDropdown)}
            variant="gray"
            size="sm"
            disabled={!canEdit || isLocked}
            style={{
              opacity: canEdit && !isLocked ? 1 : 0.5,
              cursor: canEdit && !isLocked ? "pointer" : "not-allowed",
            }}
          >
            Add +
          </Button>

          {showAddDropdown && canEdit && (
            <AddElementsDropdown
              isPart={isPart}
              isSection={isSection}
              isStep={isStep}
              isSubstep={isSubstep}
              onItemClick={(type) => {
                setShowAddDropdown(false);
                if (onAddElement) {
                  onAddElement(type);
                }
              }}
            />
          )}

          <div style={{ marginLeft: "-13px" }}>
            <TablesDropdown
              showDropdown={showTablesDropdown}
              setShowDropdown={setShowTablesDropdown}
              disableBarButtons={!canAccessTables}
              handleDrpdnClick={handleDrpdnClick}
              versionHistoryHandler={
                canOpenVersionHistory
                  ? () => setShowVersionHistory(true)
                  : null
              }
              dropdownRef={tablesDropdownRef}
              isAccessible={canAccessTables}
              buttonStyle={{ fontSize: "12px" }}
            />
          </div>
        </div>

        {/* Centered Title */}
        <div style={{ flexGrow: 1, textAlign: "center" }}>
          <h1
            className="section-element-title"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {(() => {
              const rawTitle = title.toLowerCase().includes("subs")
                ? selectedSubstepTitle
                : title;

              const cleanedTitle = rawTitle.replace(
                /^(.*?)\s+\1\s+(\d+)$/i,
                "$1 $2"
              );

              return `${cleanedTitle} Selected`;
            })()}

            {isPart && (
              <Button
                onClick={() => {
                  if (!canEdit || isLocked) return;
                  if (onEditPart) {
                    onEditPart();
                  }
                }}
                size="sm"
                style={{ marginLeft: "10px" }}
                disabled={!canEdit || isLocked}
              >
                Edit
              </Button>
            )}

            {isSelectedPage && (
              <>
                {isSection && !isPart && (
                  <Button
                    onClick={() => {
                      if (!canEdit || isLocked) return;
                      if (onEditSection) {
                        onEditSection();
                      }
                    }}
                    size="sm"
                    style={{ marginLeft: "10px" }}
                    disabled={!canEdit || isLocked}
                  >
                    Edit
                  </Button>
                )}

                {isStep && (
                  <Button
                    onClick={() => {
                      if (!canEdit || isLocked) return;
                      if (onEditStep) {
                        onEditStep();
                      }
                    }}
                    size="sm"
                    style={{ marginLeft: "10px" }}
                    disabled={!canEdit || isLocked}
                  >
                    Edit
                  </Button>
                )}

                {isSubstep && (
                  <Button
                    onClick={() => {
                      if (!canEdit || isLocked) return;
                      if (onEditSubstep) {
                        onEditSubstep();
                      }
                    }}
                    size="sm"
                    style={{ marginLeft: "10px" }}
                    disabled={!canEdit || isLocked}
                  >
                    Edit
                  </Button>
                )}

                {isSectionElement && (
                  <Button
                    onClick={() => {
                      if (!canEdit || isLocked) return;
                      if (onEditSectionElement) {
                        onEditSectionElement();
                      }
                    }}
                    size="sm"
                    style={{ marginLeft: "10px" }}
                    disabled={!canEdit || isLocked}
                  >
                    Edit
                  </Button>
                )}
              </>
            )}

            {editSubstepbtn && (
              <Button
                onClick={() => {
                  if (!canEdit || isLocked) return;
                  if (typeof editSubstepbtn === "function") {
                    editSubstepbtn();
                  }
                }}
                size="sm"
                style={{ marginLeft: "10px" }}
                disabled={!canEdit || isLocked}
              >
                Edit
              </Button>
            )}
          </h1>
        </div>

        {/* Right Side: Edit, Move, Copy, Delete Buttons */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {buttons.map((button, index) => (
            <Button
              key={index}
              onClick={canEdit && !isLocked ? button.onClick : undefined}
              size="sm"
              disabled={!canEdit || button.disabled || isLocked}
              style={{
                ...button.style,
                opacity: canEdit && !isLocked ? button.style?.opacity ?? 1 : 0.5,
                cursor: canEdit && !isLocked
                  ? button.style?.cursor ?? "pointer"
                  : "not-allowed",
              }}
            >
              {button.label}
            </Button>
          ))}

          {isSelectedPage && (
            <span>
              {isPart && (
                <Button
                  onClick={() => {
                    if (!canEdit || isLocked) return;
                    handleCopyWithTaskCheck({
                      sourceDocument: {
                        partId: effectivePartId,
                        revisionId: effectiveRevisionId,
                        partNumber: effectivePartNumber,
                        revision: effectiveRevision,
                        type: type || title.split(" ")[0] || "Document",
                        description: "",
                        version_id_ref: version_id_ref,
                      },
                    });
                  }}
                  variant="outline-blue"
                  size="sm"
                  disabled={!canEdit || isLocked}
                  style={{
                    opacity: canEdit && !isLocked ? 1 : 0.5,
                    cursor: canEdit && !isLocked ? "pointer" : "not-allowed",
                  }}
                >
                  Copy & Replace
                </Button>
              )}
              {!isPart && (
                <span style={{ display: "flex", gap: "10px" }}>
                  <Button
                    disabled={!canEdit || isLocked}
                    style={{
                      opacity: canEdit && !isLocked ? 1 : 0.5,
                      cursor: canEdit && !isLocked ? "pointer" : "not-allowed",
                    }}
                    onClick={async () => {
                      if (!canEdit || isLocked) return;

                      // Fetch revision details first
                      let revisionDetails = {};

                      // Fetch revision data to get complete details
                      if (effectiveRevisionId) {
                        try {
                          const response = await dispatch(
                            getProcedureRevisionById(effectiveRevisionId)
                          ).unwrap();
                          const revData = response?.results || response;
                          // Extract revision details
                          revisionDetails = {
                            type: revData?.part_data?.type?.name,
                            partNumber: revData?.part_data?.part_number,
                            description: revData?.part_data?.description,
                            status: revData?.status_changes.find(
                              (sc) => sc.is_latest
                            )?.status_changed_to,
                          };
                        } catch (error) {
                          console.error(
                            "Error fetching revision details:",
                            error
                          );
                          // Use fallback values if fetch fails
                        }
                      }

                      // Handle move for sections, steps, substeps, etc.
                      if (isSection) {
                        // Dispatch openMove with node info for section
                        await handleMoveWithTaskCheck({
                          moveNode: {
                            nodeId: sectionId,
                            nodeType: "section",
                            nodeTitle: title, // Section title like "Section 1"
                            sectionIndex: sectionIndex,
                          },
                          moveDocument: {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: revisionDetails.partNumber,
                            revision: effectiveRevision,
                            type: revisionDetails.type,
                            description: revisionDetails.description,
                            status: revisionDetails.status,
                          },
                          moveTargetDocument: null, // null for same-document move, can be set later for cross-doc
                        });
                      } else if (isStep) {
                        // Dispatch openMove with node info for step
                        await handleMoveWithTaskCheck({
                          moveNode: {
                            nodeId: stepId,
                            nodeType: "step",
                            nodeTitle: title, // Step title like "Step 1"
                            stepId: stepId,
                            stepIndex: stepIndex,
                            globalStepNumber: globalStepNumber,
                            sectionId: sectionId,
                            sectionIndex: sectionIndex,
                          },
                          moveDocument: {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: revisionDetails.partNumber,
                            revision: effectiveRevision,
                            type: revisionDetails.type,
                            description: revisionDetails.description,
                            status: revisionDetails.status,
                          },
                          moveTargetDocument: null,
                        });
                      } else if (isSubstep) {
                        // Dispatch openMove with node info for substep
                        await handleMoveWithTaskCheck({
                          moveNode: {
                            nodeId: effectiveSubstepIdRef,
                            nodeType: "substep",
                            nodeTitle: title,
                            sectionId: sectionId || section_id_ref,
                            stepId: stepId || step_id_ref,
                          },
                          moveDocument: {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: revisionDetails.partNumber,
                            revision: effectiveRevision,
                            type: revisionDetails.type,
                            description: revisionDetails.description,
                            status: revisionDetails.status,
                          },
                          moveTargetDocument: null,
                        });
                      } else if (isSectionElement) {
                        // Child node move (Attachment, Video, Rich Text, Canvas, Form)
                        const lowerTitle = (title || "").toLowerCase();
                        const childTypes = [
                          "attachment",
                          "video",
                          "rich text",
                          "rich-text",
                          "canvas",
                          "form",
                          "canva",
                        ];
                        const matched = childTypes.find((ct) =>
                          lowerTitle.startsWith(ct)
                        );
                        if (matched) {
                          const normalizedType =
                            matched === "rich text" ? "rich-text" : matched;

                          await handleMoveWithTaskCheck({
                            moveNode: {
                              nodeId: child_node_id,
                              nodeType: normalizedType,
                              nodeTitle: title,
                              sectionId: sectionId || section_id_ref,
                              stepId: stepId || step_id_ref,
                              substepId: effectiveSubstepIdRef || null,
                            },
                            moveDocument: {
                              partId: effectivePartId,
                              revisionId: effectiveRevisionId,
                              partNumber: revisionDetails.partNumber,
                              revision: effectiveRevision,
                              type: revisionDetails.type,
                              description: revisionDetails.description,
                              status: revisionDetails.status,
                            },
                            moveTargetDocument: null,
                          });
                        } else {
                        }
                      } else {
                      }
                    }}
                    variant="outline-blue"
                    size="sm"
                  >
                    Move
                  </Button>
                  <Button
                    disabled={!canEdit || isLocked}
                    style={{
                      opacity: canEdit && !isLocked ? 1 : 0.5,
                      cursor: canEdit && !isLocked ? "pointer" : "not-allowed",
                    }}
                    onClick={async () => {
                      if (!canEdit || isLocked) return;
                      // Handle copy for sections, steps, substeps, etc.
                      if (isSection) {
                        // Dispatch openCopyReplace with node info for section
                        await handleCopyWithTaskCheck({
                          copyMode: "node",
                          sourceDocument: {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: effectivePartNumber,
                            revision: effectiveRevision,
                            type: type || "Document",
                            description: "",
                          },
                          sourceNode: {
                            nodeId: sectionId,
                            nodeType: "section",
                            nodeTitle: title, // Section title like "Section 1"
                            sectionIndex: sectionIndex,
                            // Add section data if needed
                          },
                        });
                      } else if (isStep) {
                        // Dispatch openCopyReplace with node info for step
                        await handleCopyWithTaskCheck({
                          copyMode: "node",
                          sourceDocument: {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: effectivePartNumber,
                            revision: effectiveRevision,
                            type: type || "Document",
                            description: "",
                          },
                          sourceNode: {
                            nodeId: stepId,
                            nodeType: "step",
                            nodeTitle: title, // Step title like "Step 1"
                            stepId: stepId,
                            stepIndex: stepIndex,
                            globalStepNumber: globalStepNumber,
                            sectionId: sectionId,
                            sectionIndex: sectionIndex,
                          },
                        });
                      } else if (isSubstep) {
                        // Dispatch openCopyReplace with node info for substep
                        await handleCopyWithTaskCheck({
                          copyMode: "node",
                          sourceDocument: {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: effectivePartNumber,
                            revision: effectiveRevision,
                            type: type || "Document",
                            description: "",
                          },
                          sourceNode: {
                            nodeId: effectiveSubstepIdRef,
                            nodeType: "substep",
                            nodeTitle: title,
                            sectionId: sectionId || section_id_ref,
                            stepId: stepId || step_id_ref,
                            globalStepNumber: globalStepNumber,
                          },
                        });
                      } else if (isSectionElement) {
                        // Child node copy (Attachment, Video, Rich Text, Canvas, Form)
                        const lowerTitle = (title || "").toLowerCase();
                        const childTypes = [
                          "attachment",
                          "video",
                          "rich text",
                          "rich-text",
                          "canvas",
                          "form",
                          "canva",
                        ];
                        const matched = childTypes.find((ct) =>
                          lowerTitle.startsWith(ct)
                        );
                        if (matched) {
                          const normalizedType =
                            matched === "rich text" ? "rich-text" : matched;
                          await handleCopyWithTaskCheck({
                            copyMode: "node",
                            sourceDocument: {
                              partId: effectivePartId,
                              revisionId: effectiveRevisionId,
                              partNumber: effectivePartNumber,
                              revision: effectiveRevision,
                              type: type || "Document",
                              description: "",
                            },
                            sourceNode: {
                              nodeId: child_node_id,
                              nodeType: normalizedType,
                              nodeTitle: title,
                              sectionId: sectionId || section_id_ref,
                              stepId: stepId || step_id_ref,
                              substepId: effectiveSubstepIdRef || null,
                              globalStepNumber: globalStepNumber,
                              substepIndex: substepIndex,
                            },
                          });
                        } else {
                        }
                      } else {
                      }
                    }}
                    variant="outline-blue"
                    size="sm"
                  >
                    Copy
                  </Button>
                  <Button
                    disabled={!canEdit || isLocked}
                    style={{
                      opacity: canEdit && !isLocked ? 1 : 0.5,
                      cursor: canEdit && !isLocked ? "pointer" : "not-allowed",
                    }}
                    onClick={async () => {
                      if (!canEdit || isLocked) return;

                      // Fetch revision details to get description
                      let deleteRevisionDetails = {};
                      if (effectiveRevisionId) {
                        try {
                          const response = await dispatch(
                            getProcedureRevisionById(effectiveRevisionId)
                          ).unwrap();
                          const revData = response?.results || response;
                          deleteRevisionDetails = {
                            description: revData?.part_data?.description || "",
                          };
                        } catch (error) {
                          console.error(
                            "Error fetching revision details for delete:",
                            error
                          );
                        }
                      }

                      // Handle delete for sections, steps, substeps, etc.
                      if (isSection) {
                        await handleDeleteWithTaskCheck(
                          {
                            nodeId: sectionId,
                            nodeType: "section",
                            nodeTitle: title,
                            sectionIndex: sectionIndex,
                          },
                          {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: effectivePartNumber,
                            revision: effectiveRevision,
                            type: type || "Document",
                            description: deleteRevisionDetails.description || "",
                          }
                        );
                      } else if (isStep) {
                        await handleDeleteWithTaskCheck(
                          {
                            nodeId: stepId,
                            nodeType: "step",
                            nodeTitle: title,
                            sectionId: sectionId,
                            stepId: stepId,
                            stepIndex: stepIndex,
                            globalStepNumber: globalStepNumber,
                            sectionIndex: sectionIndex,
                          },
                          {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: effectivePartNumber,
                            revision: effectiveRevision,
                            type: type || "Document",
                            description: deleteRevisionDetails.description || "",
                          }
                        );
                      } else if (isSubstep) {
                        await handleDeleteWithTaskCheck(
                          {
                            nodeId: effectiveSubstepIdRef,
                            nodeType: "substep",
                            nodeTitle: title,
                            sectionId: sectionId || section_id_ref,
                            stepId: stepId || step_id_ref,
                          },
                          {
                            partId: effectivePartId,
                            revisionId: effectiveRevisionId,
                            partNumber: effectivePartNumber,
                            revision: effectiveRevision,
                            type: type || "Document",
                            description: deleteRevisionDetails.description || "",
                          }
                        );
                      } else if (isSectionElement) {
                        const lowerTitle = (title || "").toLowerCase();
                        const childTypes = [
                          "attachment",
                          "video",
                          "rich text",
                          "rich-text",
                          "canvas",
                          "form",
                          "canva",
                        ];
                        const matched = childTypes.find((ct) =>
                          lowerTitle.startsWith(ct)
                        );
                        if (matched) {
                          const normalizedType =
                            matched === "rich text" ? "rich-text" : matched;
                          await handleDeleteWithTaskCheck(
                            {
                              nodeId: child_node_id,
                              nodeType: normalizedType,
                              nodeTitle: title,
                              sectionId: sectionId || section_id_ref,
                              stepId: stepId || step_id_ref,
                              substepId: effectiveSubstepIdRef || null,
                            },
                            {
                              partId: effectivePartId,
                              revisionId: effectiveRevisionId,
                              partNumber: effectivePartNumber,
                              revision: effectiveRevision,
                              type: type || "Document",
                              description: deleteRevisionDetails.description || "",
                            }
                          );
                        } else {
                        }
                      } else {
                      }
                    }}
                    variant="outline-red"
                    size="sm"
                  >
                    Delete
                  </Button>
                </span>
              )}
            </span>
          )}
        </div>
      </header>
      {openTasksState.isOpen && (
        <OpenTasksPopup
          closeModal={(close) => {
            setOpenTasksState({
              isOpen: false,
              ltree_path: null,
              deleteNodeData: null,
              deleteDocumentData: null,
              copyData: null,
              moveData: null,
              operation: null,
              title: null
            });
          }}
          procedure_ltree_ref={openTasksState.ltree_path}
          title={openTasksState.title}
          isContinueDisabled={openTasksState.operation === "delete"}
          onContinue={() => {
            if (openTasksState.deleteNodeData || openTasksState.deleteDocumentData) {
              dispatch(openDelete({
                deleteDocument: openTasksState.deleteDocumentData,
                deleteNode: openTasksState.deleteNodeData
              }));
            } else if (openTasksState.copyData) {
              dispatch(openCopyReplace(openTasksState.copyData));
            } else if (openTasksState.moveData) {
              dispatch(openMove(openTasksState.moveData));
            }
          }}
        />
      )}
      <TablePopups showTablePopup={showTablePopup} closePopup={closeTablePopup} />
      {showVersionHistory && (
        <VersionHistoryPopup
          onClose={() => setShowVersionHistory(false)}
          title={versionHistoryPopupTitle}
          versionHistories={mergedVersionHistories}
          mode="edit"
          versionId={versionHistoryFallbackVersionId}
          initialFilterType="all"
          historyFilterDropdownDisabled={historyFilterDropdownDisabled}
          breadcrumbText={versionHistoryBreadcrumbFallback}
          revisionTreeNodes={allRevisionNodes}
          procedureRevisionIdForNodeHistory={effectiveRevisionId}
          restrictNodeHistoryToProcedureRevision={allVersionHistories.length === 0}
          nodeId={versionHistoryNodeId || undefined}
          nodeIdField={versionHistoryNodeIdField}
          nodeHistoryMatchIds={
            Array.isArray(versionHistoryMatchIds)
              ? versionHistoryMatchIds
              : undefined
          }
          nodeHistorySectionOrdinal={
            versionHistorySectionOrdinal != null &&
            Number.isFinite(Number(versionHistorySectionOrdinal))
              ? Number(versionHistorySectionOrdinal)
              : undefined
          }
          nodeHistoryStepOrdinal={
            versionHistoryStepOrdinal != null &&
            Number.isFinite(Number(versionHistoryStepOrdinal))
              ? Number(versionHistoryStepOrdinal)
              : undefined
          }
          nodeHistorySubstepLetterSuffix={
            typeof versionHistorySubstepLetterSuffix === "string" &&
            versionHistorySubstepLetterSuffix.trim() !== ""
              ? versionHistorySubstepLetterSuffix.trim()
              : undefined
          }
          nodeHistoryLeafMediaOrdinal={
            versionHistoryLeafMediaOrdinal != null &&
            Number.isFinite(Number(versionHistoryLeafMediaOrdinal))
              ? Number(versionHistoryLeafMediaOrdinal)
              : undefined
          }
          nodeHistoryLeafMediaPhraseRegex={
            typeof versionHistoryLeafMediaPhraseRegex === "string" &&
            versionHistoryLeafMediaPhraseRegex.trim() !== ""
              ? versionHistoryLeafMediaPhraseRegex.trim()
              : undefined
          }
          viewActionDisabled={!canNavigateVersionPreview}
          onViewDetails={(row) => {
            const auditId = row.audit_trail_id || row.audit_trail_id_ref;
            const path = getVersionHistoryAuditPreviewPath(
              auditId,
              row.action,
            );
            if (path) {
              navigate(path);
            } else {
              alert("No preview available for this version");
            }
          }}
          exportCsvDisabled={versionHistoryTableLoading}
          exportCsvButtonTitle="Export will be enabled once all nodes are loaded."
        />
      )}
    </>
  );
};

export default NodeAppBar;
